import {
  Component,
  computed,
  inject,
  input,
  model,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

import { ButtonComponent } from 'src/app/components/button/button.component';
import { IconComponent } from 'src/app/components/icon/icon.component';
import { InputWrapperComponent } from '../input-wrapper/input-wrapper.component';
import { InputRegexDirective } from '../../directives/input-regex.directive';
import { UppercaseDirective } from '../../directives/uppercase.directive';

export interface ValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

@Component({
  selector: 'app-search-input',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    ButtonComponent,
    IconComponent,
    InputWrapperComponent,
    InputRegexDirective,
    NgxMaskDirective,
    UppercaseDirective,
  ],
  templateUrl: './search-input.component.html',
  styleUrls: ['./search-input.component.scss'],
  providers: [provideNgxMask()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchInputComponent implements ControlValueAccessor {
  readonly ngControl = inject(NgControl, { optional: true, self: true });
  readonly id = input<string | undefined>(undefined);

  value = model<string>('');
  inputLabel = input<string>('');
  placeholder = input<string>('');
  mask = input<string>('');
  disabledSearch = input<boolean>(false);
  allowedCharsRegex = input<string | undefined>();
  customValidator = input<(value: string) => ValidationResult>();

  searchTriggered = output<string>();

  readonly validationResult = computed(() => {
    const validator = this.customValidator();
    const val = this.value();
    return validator && val ? validator(val) : null;
  });

  readonly isInputInvalid = computed(() => {
    const customInvalid = this.validationResult() ? !this.validationResult()?.isValid : false;
    const formInvalid = !!(this.ngControl?.invalid && this.ngControl?.touched);
    return customInvalid || formInvalid;
  });

  readonly validationMessage = computed(() => this.validationResult()?.errorMessage ?? '');

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(val: string): void {
    this.value.set(val ?? '');
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onInputChange(event: Event): void {
    const newValue = (event.target as HTMLInputElement).value;
    this.value.set(newValue);
    this.onChange(newValue);
  }

  handleSearch(): void {
    if (this.disabledSearch() || this.isInputInvalid()) return;
    this.searchTriggered.emit(this.value());
  }

  handleClearSearch(): void {
    this.value.set('');
    this.onChange('');
    this.onTouched();
    this.handleSearch();
  }

  onKeyDown(event: KeyboardEvent): void {
    const regexStr = this.allowedCharsRegex();
    if (this.mask() || !regexStr) return;

    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
    ];
    if (event.ctrlKey || event.metaKey || allowedKeys.includes(event.key)) return;

    if (!new RegExp(regexStr).test(event.key)) {
      event.preventDefault();
    }
  }

  onPaste(event: ClipboardEvent): void {
    const regexStr = this.allowedCharsRegex();
    if (this.mask() || !regexStr) return;

    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text/plain') ?? '';
    const regex = new RegExp(regexStr);
    const cleanedText = pastedText
      .split('')
      .filter((char) => regex.test(char))
      .join('');

    this.value.set(cleanedText);
    this.onChange(cleanedText);
  }
}
